import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Notification } from '../interfaces/notification.interface';
import { DateTime } from 'luxon';
import { Router } from '@angular/router';
import { trackById } from '@vex/utils/track-by';
import { VexDateFormatRelativePipe } from '@vex/pipes/vex-date-format-relative/vex-date-format-relative.pipe';
import { MatRippleModule } from '@angular/material/core';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppNotification, NotificationService } from '../../../../../services/notification.service';

// Best-effort icon/color per notification_type — falls back to a generic bell.
const TYPE_STYLE: Record<string, { icon: string; colorClass: string }> = {
  design_studio_batch: { icon: 'mat:auto_awesome', colorClass: 'text-purple-600' },
  chat: { icon: 'mat:chat', colorClass: 'text-primary-600' },
  order: { icon: 'mat:shopping_basket', colorClass: 'text-primary-600' },
  subscription: { icon: 'mat:verified_user', colorClass: 'text-green-600' },
};
const DEFAULT_STYLE = { icon: 'mat:notifications', colorClass: 'text-amber-600' };

function toViewModel(n: AppNotification): Notification {
  const style = TYPE_STYLE[n.notification_type ?? ''] ?? DEFAULT_STYLE;
  return {
    id: n.id,
    icon: style.icon,
    colorClass: style.colorClass,
    label: n.subject || n.message,
    description: n.subject ? n.message : undefined,
    datetime: DateTime.fromISO(n.timestamp),
    read: n.is_read,
    link: n.link,
    data: n.data,
  };
}

@Component({
  selector: 'vex-toolbar-notifications-dropdown',
  templateUrl: './toolbar-notifications-dropdown.component.html',
  styleUrls: ['./toolbar-notifications-dropdown.component.scss'],
  standalone: true,
  imports: [
    MatButtonModule,
    MatMenuModule,
    MatIconModule,
    NgFor,
    NgIf,
    MatRippleModule,
    NgClass,
    VexDateFormatRelativePipe,
  ],
})
export class ToolbarNotificationsDropdownComponent implements OnInit, OnDestroy {
  notifications: Notification[] = [];
  loading = true;

  trackById = trackById;

  private destroy$ = new Subject<void>();

  constructor(
    private notificationService: NotificationService,
    private router: Router,
    private cd: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.notificationService.getNotifications({ page_size: 20 }).subscribe({
      next: (results) => {
        this.notifications = results.map(toViewModel);
        this.loading = false;
        this.cd.markForCheck();
      },
      error: () => {
        this.loading = false;
        this.cd.markForCheck();
      },
    });

    // Keep the open dropdown fresh if a new notification arrives while it's open.
    this.notificationService.updates$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.notificationService.getNotifications({ page_size: 20 }).subscribe((results) => {
        this.notifications = results.map(toViewModel);
        this.cd.markForCheck();
      });
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  open(notification: Notification) {
    if (!notification.read) {
      notification.read = true;
      this.notificationService.markRead(notification.id).subscribe();
    }

    const target = notification.data?.url || notification.link;
    if (!target) {
      return;
    }

    try {
      const url = new URL(target, window.location.origin);
      if (url.origin === window.location.origin) {
        this.router.navigateByUrl(url.pathname + url.search);
      } else {
        window.open(target, '_blank');
      }
    } catch {
      // Not a full URL — treat as an app-relative path.
      this.router.navigateByUrl(target);
    }
  }

  markAllRead(event: Event) {
    event.stopPropagation();
    const unread = this.notifications.filter((n) => !n.read);
    unread.forEach((n) => (n.read = true));
    this.cd.markForCheck();
    unread.forEach((n) => this.notificationService.markRead(n.id).subscribe());
  }
}
